import { TableRowClassKey, Tooltip } from '@material-ui/core';
import { isEmpty, isNaN } from 'lodash-es';
import MuiVirtualizedTable, { IHeaderClickProps } from 'mui-virtualized-table';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { convertECurrencyTypeToSymbol, ECurrencyType } from '~/src/dash/api/exchange-rates';
import { ReportTypeID } from '~/src/dash/apiV2/reporting/reportTypes';
import {
  sortByBooleanString,
  sortByDate,
  sortByDollarRange,
  sortByNumber,
  sortByString,
} from '~/src/dash/utils/analyticsSorter';
import {
  formatFunctions,
  IFormatFunction,
  IReportDataRow,
} from '~/src/dash/views/reporting/Preview/common';
import { useReportPreviewStore } from '~/src/dash/views/reporting/Preview/context';

const columnHeight = 48; // set by MuiVirtualizedTable, cannot be changed otherwise it messes up the render
const columnWidth = {
  base: 150,
  wide: 280,
  extraWide: 320,
};
const getColumnWidth = (key: string, type: string) => {
  if (['biddername', 'sitename', 'campaignname', 'content_series', 'content_title'].includes(key))
    return columnWidth.extraWide;
  if (type !== 'number' && type !== 'boolean' && type !== 'date') return columnWidth.wide;
  return columnWidth.base;
};

interface IColumn {
  cell: (rowData: Record<string, any>) => string | JSX.Element;
  header: string | JSX.Element;
  isDimension: boolean;
  name: keyof IReportDataRow;
  sortFunc: (a: any, b: any) => void;
  width: number;
}

interface IReportTable {
  height: number;
  width: number;
}

export const ReportTable = ({ height, width }: IReportTable) => {
  const {
    data,
    getQueryParamValue,
    publisher,
    queryParams,
    reportTypeConfig,
    updateQueryParams,
    isDataOversized,
  } = useReportPreviewStore();

  const [containerSize, setContainerSize] = useState<Array<number>>([height, width]);
  const [tableData, setTableData] = useState<Array<IReportDataRow>>([]);
  const [columns, setColumns] = useState<Array<IColumn>>([]);
  const [orderDirections, setOrderDirections] = useState<Record<string, 'asc' | 'desc'>>({});

  // update the table whenever the queryParams, reportTypeConfig, or width updates
  useEffect(() => {
    if (isEmpty(queryParams) || isEmpty(reportTypeConfig)) return;
    setTableConfig();
  }, [queryParams, reportTypeConfig, width]);

  // helper to decide how each column is sorted
  const getSortFunc = (sortByType: string) => {
    switch (sortByType) {
      case 'string':
        return sortByString;
      case 'number':
        return sortByNumber;
      case 'date':
        return sortByDate;
      case 'boolean':
        return sortByBooleanString;
      case 'currencyRange':
        return sortByDollarRange;
      default:
        return sortByString;
    }
  };

  const formatCellText = (text: string = '', placement: 'bottom-start' | 'bottom-end') => (
    <Tooltip title={text} placement={placement}>
      <span>{text}</span>
    </Tooltip>
  );

  // helper to format dimensions
  const formatDimensions = (dimensions: Array<string>): Array<IColumn> => {
    // calculate the width for each dimension
    const dimensionToWidth = dimensions.reduce((acc: Record<string, number>, dimension: string) => {
      const colConfig = reportTypeConfig.Dimensions[dimension] || {};
      acc[dimension] = getColumnWidth(colConfig.Key, colConfig.SortByType);
      return acc;
    }, {});

    // calculate the width that all dimensions will take up
    const totalWidth = Object.values(dimensionToWidth).reduce((acc, width) => acc + width, 0);

    // limit the dimensions so that at least 2 metric columns can display
    const maxTotalWidth = width - columnWidth.base * 2;

    return (
      dimensions
        // push date column at render as the first column
        .sort((a: string, b: string) => {
          if (['hour', 'day', 'month'].includes(a)) return -1;
          if (['hour', 'day', 'month'].includes(b)) return 1;
          return a.toLowerCase().localeCompare(b.toLowerCase());
        })
        // map each dimension to its column config that can be used by MuiVirtualizedTable
        .map((dimension: string): IColumn => {
          const colConfig = reportTypeConfig.Dimensions[dimension] || {};
          // if the dimensions totalWidth is more than their maxWidth
          // adjust the width of each column so that they will all fit into maxWidth
          // otherwise, use the original width that we calculated above
          const colWidth =
            totalWidth > maxTotalWidth
              ? Math.floor(maxTotalWidth / dimensions.length)
              : dimensionToWidth[dimension];
          return {
            isDimension: true,
            name: colConfig.Key,
            header: formatCellText(colConfig.Label, 'bottom-start'),
            cell: (rowData) => formatCellText(rowData[colConfig.Key], 'bottom-start'),
            sortFunc: getSortFunc(colConfig.SortByType),
            width: colWidth,
          };
        })
    );
  };

  // helper to format dimensions
  const formatMetrics = (metrics: Array<string>): Array<IColumn> =>
    metrics.map((metric: string): IColumn => {
      const colConfig = metric.includes('preset_aggregate')
        ? reportTypeConfig.Dimensions[metric] || {}
        : reportTypeConfig.Metrics[metric] || {};

      const colWidth = getColumnWidth(colConfig.Key, colConfig.SortByType);

      return {
        isDimension: false,
        name: colConfig.Key,
        header: formatCellText(
          metric.includes('preset_aggregate') ? `Unique Users ${colConfig.Label}` : colConfig.Label,
          'bottom-end',
        ),
        cell: (rowData) => formatCellText(rowData[colConfig.Key], 'bottom-end'),
        sortFunc: getSortFunc(colConfig.SortByType),
        width: colWidth,
      };
    });

  // helper to get all the table columns which includes dimensions first, then metrics
  const getColumns = (): Array<IColumn> => {
    if (isEmpty(reportTypeConfig)) return [];

    // filter dimensions
    const dimensions = getQueryParamValue('dimensions');
    const filteredDimensions = dimensions.filter(
      (dimension: string) => !dimension.includes('preset_aggregate'),
    );

    // if bidder_testing is a dimension, replace that with
    // ab_testing_config_name and ab_testing_test_name for the columns
    if (filteredDimensions.find((key: string) => key === 'bidder_testing')) {
      filteredDimensions.splice(
        filteredDimensions.findIndex((key: string) => key === 'bidder_testing'),
        1,
      );
      filteredDimensions.push('ab_testing_config_name', 'ab_testing_test_name');
    }

    // filter metrics
    // if dimensions include any preset_aggregate values, add those to metrics
    const metrics = getQueryParamValue('metrics');
    const presetAggregateDimensions = dimensions.filter((dimension: string) =>
      dimension.includes('preset_aggregate'),
    );
    const filteredMetrics =
      presetAggregateDimensions.length > 0 ? presetAggregateDimensions : metrics;

    // format the final array of columns, listing dimensions first then metrics
    return [...formatDimensions(filteredDimensions), ...formatMetrics(filteredMetrics)];
  };

  // helper to format the table data for display
  const formatData = (
    data: Array<Record<string, any>>,
    columns: Array<IColumn>,
    formatFunctions: IFormatFunction[],
  ): object[] => {
    // get the correct exchangeRate and currency
    const exchangeRate =
      Number(queryParams?.exchangerate) || publisher?.ExchangeRate?.ExchangeValueToUSD || 1;
    const currency =
      queryParams?.currencydisplayed !== null && queryParams?.currencydisplayed !== undefined
        ? Number(queryParams?.currencydisplayed)
        : Number(publisher?.CurrencyDisplayed);
    const currencySymbol = convertECurrencyTypeToSymbol(
      !isNaN(currency) ? currency : ECurrencyType.CURRENCY_TYPE_USD,
    );

    // format data based on the regexes in formatFunctions
    const formattedData = data.map((datum: IReportDataRow) => {
      return {
        ...datum,
        ...columns.reduce((acc: Record<string, any>, column) => {
          // find the format function that matches the column name
          const key = column.name;
          const formatFunc = formatFunctions.find((func: IFormatFunction) => func.regex.test(key));

          // if there is matched function, use it to format the column value
          if (formatFunc) {
            switch (formatFunc.name) {
              case 'formatRate':
                acc[key] = formatFunc.formattingFunction(datum[key], reportTypeConfig.ID);
                break;
              case 'formatCurrency':
                acc[key] = formatFunc.formattingFunction(
                  datum[key],
                  ReportTypeID.UnknownReport,
                  exchangeRate,
                  currencySymbol,
                );
                break;
              default:
                acc[key] = formatFunc.formattingFunction(datum[key]);
                break;
            }

            // otherwise keep the value as it is
          } else {
            acc[key] = datum[key];
          }
          return acc;
        }, {}),
      };
    });

    return formattedData;
  };

  // determine the table data, columns, and order
  const setTableConfig = () => {
    // get all the columns, including dimensions and metrics
    const cols = getColumns();

    // determine the width & height of the container of the MUITable in order to
    // get the correct border-right when the columns dont fill up the entire browser's screen
    const totalHeight = columnHeight * (data.length + 1); // +1 for header
    const totalWidth = cols.reduce((acc, col) => acc + col.width, 0);

    // create a map of all the columns in the param orderBys and their sort order
    const orderBys = getQueryParamValue('orderBys');
    const ordersByMap = orderBys.reduce(
      (acc: { [key: string]: 'asc' | 'desc' }, orderBy: string) => {
        const [key, order] = orderBy.split(' ');
        acc[key] = order.toLowerCase() === 'asc' ? 'asc' : 'desc';
        return acc;
      },
      {},
    );

    unstable_batchedUpdates(() => {
      setContainerSize([
        totalHeight < height ? totalHeight : 0,
        totalWidth < width ? totalWidth : 0,
      ]);
      setTableData(formatData(data, cols, formatFunctions));
      setColumns(cols);
      setOrderDirections(ordersByMap);
    });
  };

  // handler for when a user clicks on a column header
  const onHeaderClick = (
    _event: React.MouseEvent<HTMLElement>,
    props: IHeaderClickProps<TableRowClassKey>,
  ) => {
    const { name, sortFunc, isDimension } = props.column as Record<string, any>;
    const newOrder = orderDirections[name] === 'asc' ? 'desc' : 'asc';

    if (isDataOversized && !isDimension) {
      updateQueryParams({
        report_order_by: `${name} ${newOrder}`,
      });
    } else {
      unstable_batchedUpdates(() => {
        setTableData(
          formatData(
            data.sort((a: IReportDataRow, b: IReportDataRow) => {
              // @ts-ignore
              return sortFunc(a[name], b[name]) * (newOrder === 'asc' ? -1 : 1);
            }),
            columns,
            formatFunctions,
          ),
        );
        setOrderDirections({ [name]: newOrder });
      });
    }
  };

  return (
    // this div is used to provide the right border for the table when
    // we dont have enough columns to fill up the entire screen
    <div
      id='reporting-preview-table-container'
      style={{
        height: containerSize[0] > 0 ? `${containerSize[0]}px` : 'fit-content',
        width: `${containerSize[1]}px`,
      }}
    >
      {columns.length > 0 ? (
        <MuiVirtualizedTable
          data={tableData}
          columns={columns}
          // @ts-ignore
          onHeaderClick={onHeaderClick}
          orderDirection={orderDirections}
          orderBy={Object.keys(orderDirections)}
          height={height}
          width={width}
          fixedRowCount={1}
          fixedColumnCount={
            columns.filter((column: Record<string, any>) => column.isDimension).length
          }
          includeHeaders={true}
        />
      ) : (
        <div />
      )}
    </div>
  );
};
